import { Routes, Route, Navigate } from 'react-router-dom';
import { LocationProvider } from './hooks/useLocationContext';
import { ToastProvider } from './components/Toast';
import ProgressBar from './components/ProgressBar';
import { RequireAuth, GuestOnly } from './components/RouteGuards';
import Layout from './components/Layout';
import DashboardOrParent from './components/DashboardOrParent';

// Auth pages (public / guest-only)
import Login from './pages/auth/Login';
import InviteSignup from './pages/auth/InviteSignup';
import SignupContactDetails from './pages/auth/SignupContactDetails';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// App pages (authenticated)
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import StudentDetail from './pages/StudentDetail';
import Locations from './pages/Locations';
import Organizations from './pages/Organizations';
import SettingsIndex from './pages/settings/index';
import PersonalInfo from './pages/settings/PersonalInfo';
import Memberships from './pages/settings/Memberships';
import Security from './pages/settings/Security';
import NotificationsSettings from './pages/settings/Notifications';
import CalendarRoute from './pages/calendar/CalendarRoute';
import Forms from './pages/Forms';
import FormBuilder from './pages/FormBuilder';
import FormFill from './pages/FormFill';
import FormFillThankYou from './pages/forms/FormFillThankYou';
import Classes from './pages/Classes';
import Team from './pages/Team';
import RunSession from './pages/RunSession';

// Messages pages
import Messages from './pages/Messages';
import MessageDetailPage from './pages/messages/MessageDetailPage';
import ObservationDetailPage from './pages/observations/ObservationDetailPage';

// Fire drills pages
import FireDrillDetailPage from './pages/fire-drills/FireDrillDetailPage';
import LiveDrillScreen from './pages/fire-drills/LiveDrillScreen';
import WrapUpScreen from './pages/fire-drills/WrapUpScreen';
import FireDrillLogPage from './pages/fire-drills/FireDrillLogPage';

// Incidents pages (staff/admin register + detail)
import IncidentRegister from './pages/incidents/IncidentRegister';
import IncidentDetail from './pages/incidents/IncidentDetail';
import IncidentFieldsAdmin from './pages/incidents/IncidentFieldsAdmin';

// Cleaning pages
import CleaningPage from './pages/cleaning/CleaningPage';

// Checklists — one tabbed destination (Today / Sign-off / Manage items). The
// three routes still resolve so deep links (incl. the sign-off notification)
// work; ChecklistsPage derives the active tab from the path.
import ChecklistsPage from './pages/checklists/ChecklistsPage';

// Learning-goals admin (Aistéar)
import LearningGoalsPage from './pages/learning-goals/LearningGoalsPage';

// Retention plan admin (Task 21 — clocks only)
import RetentionSettingsPage from './pages/retention/RetentionSettingsPage';

// Parent (kid-as-frame) pages
import ParentRoute, { ParentHomeRedirect } from './components/ParentRoute';
import ParentChildHome from './pages/parent/ParentChildHome';
import ParentChildThreads from './pages/parent/ParentChildThreads';
import FamilyInbox from './pages/parent/FamilyInbox';
import FamilyThreadPage from './pages/parent/FamilyThreadPage';
import ParentChildForms from './pages/parent/ParentChildForms';
import ParentChildDetails from './pages/parent/ParentChildDetails';
import ParentChildUpdates from './pages/parent/ParentChildUpdates';
import ParentFormFillRedirect from './pages/parent/ParentFormFillRedirect';
import ParentCalendar from './pages/calendar/ParentCalendar';
import AbsenceToCalendarRedirect from './pages/parent/AbsenceToCalendarRedirect';
import LegacyAllRedirect from './pages/parent/LegacyAllRedirect';
import ParentOnboarding from './pages/parent/ParentOnboarding';
import ChildChooser from './pages/parent/ChildChooser';
import ParentChildPickupContacts from './pages/parent/ParentChildPickupContacts';
import ParentAccountPickupContacts from './pages/parent/ParentAccountPickupContacts';
import ParentChildIncidents from './pages/parent/ParentChildIncidents';
import ParentMomentsPage from './pages/parent/ParentMomentsPage';
import ParentChildMedications from './pages/medications/ParentChildMedications';
import PolicyVault from './pages/policies/PolicyVault';
import PolicyDetail from './pages/policies/PolicyDetail';
import MyPolicies from './pages/policies/MyPolicies';
import LocationMedicationRegister from './pages/medications/LocationMedicationRegister';
import StudentMedicationPrint from './pages/medications/StudentMedicationPrint';
import AttendancePage from './pages/attendance/AttendancePage';
import StaffComplianceRegister from './pages/staff-compliance/StaffComplianceRegister';
import StaffComplianceProfile from './pages/staff-compliance/StaffComplianceProfile';
import StaffCompliancePrint from './pages/staff-compliance/StaffCompliancePrint';

function App() {
  return (
    <ToastProvider>
      <ProgressBar />
      <div className="min-h-screen bg-gray-50">
        <Routes>
          {/* ============ Public routes — accessible to anyone ============ */}
          <Route path="/auth/invite-signup" element={<InviteSignup />} />
          <Route path="/auth/contact-details" element={<SignupContactDetails />} />

          {/* ============ Guest-only routes — redirect to dashboard if logged in ============ */}
          <Route element={<GuestOnly />}>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Route>

          {/* ============ Authenticated routes — redirect to login if not logged in ============ */}
          <Route element={<RequireAuth />}>
            <Route
              path="/"
              element={
                <LocationProvider>
                  <Layout />
                </LocationProvider>
              }
            >
              {/* Flat routes (location-level users, org-admin at org level) */}
              <Route index element={<DashboardOrParent />} />
              <Route path="dashboard" element={<DashboardOrParent />} />
              <Route path="students" element={<Students />} />
              <Route path="students/:id" element={<StudentDetail />} />
              <Route path="locations" element={<Locations />} />
              <Route path="organizations" element={<Organizations />} />
              <Route path="settings" element={<SettingsIndex />} />
              <Route path="settings/profile" element={<PersonalInfo />} />
              <Route path="settings/memberships" element={<Memberships />} />
              <Route path="settings/security" element={<Security />} />
              <Route path="settings/notifications" element={<NotificationsSettings />} />
              <Route path="classes" element={<Classes />} />
              <Route path="calendar" element={<CalendarRoute />} />
              <Route path="forms" element={<Forms />} />
              <Route path="forms/new" element={<FormBuilder />} />
              <Route path="forms/:formId/edit" element={<FormBuilder />} />
              <Route path="forms/:formId/fill" element={<FormFill />} />
              <Route path="forms/:formId/thanks" element={<FormFillThankYou />} />
              <Route path="messages" element={<Messages />} />
              <Route path="messages/:messageId" element={<MessageDetailPage />} />
              <Route path="observations/:observationId" element={<ObservationDetailPage />} />
              <Route path="fire-drills" element={<FireDrillLogPage />} />
              <Route path="fire-drills/:id" element={<FireDrillDetailPage />} />
              <Route path="fire-drills/:id/live" element={<LiveDrillScreen />} />
              <Route path="fire-drills/:id/wrap-up" element={<WrapUpScreen />} />
              {/* Backwards-compatible alias for any external bookmarks /
                  shared links that used the old location-scoped URL. */}
              <Route path="locations/:locationId/fire-drills" element={<FireDrillLogPage />} />
              {/* Incident register + detail — staff/admin only (inspector-
                  facing surface). Same shape as fire-drills: "incidents"
                  resolves locationId from useLocationContext (no URL
                  param), "incidents/:incidentId" is the detail (an
                  incidentId is already globally unique, so the detail
                  route needs no location segment — same shape as
                  /fire-drills/:id). Only 3 prefixes registered (bare,
                  loc/:locId/, org/:orgId/loc/:locId/), matching fire-drills
                  and cleaning exactly — there's no bare org/:orgId/incidents
                  because the register requires a locationId that doesn't
                  exist at pure org scope (see Task 22 report for why this
                  departs from the brief's literal "four prefixes" wording). */}
              <Route path="incidents" element={<IncidentRegister />} />
              <Route path="incidents/:incidentId" element={<IncidentDetail />} />
              <Route path="locations/:locationId/incidents" element={<IncidentRegister />} />
              {/* Medication register + printable per-child record — staff/admin
                  only (the other inspector-facing surface). Same 3-prefix
                  shape as incidents: "medications" resolves locationId from
                  useLocationContext, "locations/:locationId/medications" is
                  the explicit form. The print record is keyed by studentId
                  alone (globally unique), matching incidents/:incidentId. */}
              <Route path="medications" element={<LocationMedicationRegister />} />
              {/* Policy vault. The detail route is keyed by policyId alone
                  (globally unique), matching incidents/:incidentId. */}
              <Route path="policies" element={<PolicyVault />} />
              {/* Staff/parent: whatever is waiting on ME. Never a gate. */}
              <Route path="my-policies" element={<MyPolicies />} />
              <Route path="locations/:locationId/policies" element={<PolicyVault />} />
              <Route path="policies/:policyId" element={<PolicyDetail />} />
              <Route
                path="locations/:locationId/medications"
                element={<LocationMedicationRegister />}
              />
              <Route
                path="students/:studentId/medications/print"
                element={<StudentMedicationPrint />}
              />
              <Route path="cleaning" element={<CleaningPage />} />
              <Route path="locations/:locationId/cleaning" element={<CleaningPage />} />
              {/* Daily checklists. Same 3-prefix shape as cleaning: flat
                  (resolves locationId from useLocationContext), the explicit
                  locations/:locationId/ form, and loc/:locId/ for org-admin
                  scoped into a location. Sign-off queue is admin-only,
                  server- and client-gated (see ChecklistSignOffQueue). */}
              <Route path="checklists" element={<ChecklistsPage />} />
              <Route path="locations/:locationId/checklists" element={<ChecklistsPage />} />
              <Route path="checklists/sign-off" element={<ChecklistsPage />} />
              <Route path="checklists/history" element={<ChecklistsPage />} />
              <Route path="locations/:locationId/checklists/history" element={<ChecklistsPage />} />
              <Route
                path="locations/:locationId/checklists/sign-off"
                element={<ChecklistsPage />}
              />
              {/* Checklist item template admin — same admin-only gate as
                  sign-off (see ChecklistTemplatePage). */}
              <Route path="checklists/template" element={<ChecklistsPage />} />
              <Route
                path="locations/:locationId/checklists/template"
                element={<ChecklistsPage />}
              />
              <Route path="learning-goals" element={<LearningGoalsPage />} />
              <Route path="loc/:locId/learning-goals" element={<LearningGoalsPage />} />
              <Route path="org/:orgId/loc/:locId/learning-goals" element={<LearningGoalsPage />} />
              {/* Retention plan admin (Task 21) — same 3-prefix scoped shape as
                  learning-goals; gated on isAdmin in the nav and the page. */}
              <Route path="retention" element={<RetentionSettingsPage />} />
              <Route path="loc/:locId/retention" element={<RetentionSettingsPage />} />
              <Route path="org/:orgId/loc/:locId/retention" element={<RetentionSettingsPage />} />
              {/* Staff compliance register (Garda vetting / credentials) —
                  admin-only, same 3-prefix shape as policies: flat (resolves
                  locationId from useLocationContext), locations/:locationId/
                  explicit form, and loc/:locId/ for org-admin scoped into a
                  location. Gated on isAdmin both in the nav and the page
                  itself (see StaffComplianceRegister.tsx), so a direct URL
                  hit is covered. */}
              {/* Attendance register (Tusla) — admin-only; the page gates on
                  isAdmin itself, so a direct URL hit is covered. Same three
                  scoped prefixes as staff-compliance below. */}
              <Route path="attendance" element={<AttendancePage />} />
              <Route path="loc/:locId/attendance" element={<AttendancePage />} />
              <Route path="org/:orgId/loc/:locId/attendance" element={<AttendancePage />} />
              <Route path="staff-compliance" element={<StaffComplianceRegister />} />
              <Route
                path="locations/:locationId/staff-compliance"
                element={<StaffComplianceRegister />}
              />
              <Route path="loc/:locId/staff-compliance" element={<StaffComplianceRegister />} />
              <Route
                path="org/:orgId/loc/:locId/staff-compliance"
                element={<StaffComplianceRegister />}
              />
              {/* Per-staff compliance profile — the product's first
                  per-staff-member detail page. Keyed by staffUserId alone
                  (globally unique), matching incidents/:incidentId and
                  policies/:policyId — the location segment carries no routing
                  information here. Admin can reach anyone; a staff member can
                  also reach their OWN profile (self-service add-credential),
                  enforced server-side via requireStaffComplianceOwner, not by
                  this route.

                  The scoped prefixes ARE still registered, because the links
                  that reach these pages are built with ScopedLink (the
                  register, the print button, the "Back to register" link, and
                  the "My Compliance" nav entry). ScopedLink prepends
                  /loc/:locId for an org admin and /org/:orgId/loc/:locId for a
                  super admin, so registering only the flat path made every one
                  of those links dead for those two roles: react-router matched
                  nothing and fell through to /dashboard. A LOCATION_ADMIN
                  (empty prefix) never saw it.

                  Registering the prefixes is the right fix rather than
                  un-scoping the links: dropping the prefix would also drop the
                  org admin OUT of their location scope on navigation, which is
                  what ScopedLink exists to prevent. */}
              <Route path="staff/:staffUserId/compliance" element={<StaffComplianceProfile />} />
              <Route
                path="loc/:locId/staff/:staffUserId/compliance"
                element={<StaffComplianceProfile />}
              />
              <Route
                path="org/:orgId/loc/:locId/staff/:staffUserId/compliance"
                element={<StaffComplianceProfile />}
              />
              {/* The inspector-ready print view — replaces PDF generation
                  (task-18-brief CONTROLLER ADDENDUM item 1: this product has
                  no PDF generation capability, `react-pdf` is a viewer). Same
                  access rule as the profile route above: admin or self,
                  enforced server-side — and the same three URL shapes, for the
                  same reason. */}
              <Route
                path="staff/:staffUserId/compliance/print"
                element={<StaffCompliancePrint />}
              />
              <Route
                path="loc/:locId/staff/:staffUserId/compliance/print"
                element={<StaffCompliancePrint />}
              />
              <Route
                path="org/:orgId/loc/:locId/staff/:staffUserId/compliance/print"
                element={<StaffCompliancePrint />}
              />
              {/* Per-org custom incident fields admin (Task 24) — same
                  2-prefix shape as learning-goals: org-scoped config, no
                  locationId in the URL, resolved via useLocationContext /
                  activeOrganizationId. ORG_ADMIN/SUPER_ADMIN only,
                  server-enforced (see IncidentFieldsAdmin.tsx). */}
              <Route path="incident-fields" element={<IncidentFieldsAdmin />} />
              <Route path="loc/:locId/incident-fields" element={<IncidentFieldsAdmin />} />
              <Route
                path="org/:orgId/loc/:locId/incident-fields"
                element={<IncidentFieldsAdmin />}
              />
              <Route path="team" element={<Team />} />
              {/* Run-a-session staff page (Attendance v2). Lazy-creates the
                  session by id; navigated to from class detail or "Today at
                  location" overview. */}
              <Route path="sessions/:sessionId" element={<RunSession />} />

              {/* Org-admin scoped into a location: /loc/:locId/... */}
              <Route path="loc/:locId/dashboard" element={<Dashboard />} />
              <Route path="loc/:locId/students" element={<Students />} />
              <Route path="loc/:locId/students/:id" element={<StudentDetail />} />
              <Route path="loc/:locId/classes" element={<Classes />} />
              <Route path="loc/:locId/calendar" element={<CalendarRoute />} />
              <Route path="loc/:locId/forms" element={<Forms />} />
              <Route path="loc/:locId/forms/new" element={<FormBuilder />} />
              <Route path="loc/:locId/forms/:formId/edit" element={<FormBuilder />} />
              <Route path="loc/:locId/forms/:formId/fill" element={<FormFill />} />
              <Route path="loc/:locId/forms/:formId/thanks" element={<FormFillThankYou />} />
              <Route path="loc/:locId/messages" element={<Messages />} />
              <Route path="loc/:locId/messages/:messageId" element={<MessageDetailPage />} />
              <Route
                path="loc/:locId/observations/:observationId"
                element={<ObservationDetailPage />}
              />
              <Route path="loc/:locId/team" element={<Team />} />
              <Route path="loc/:locId/sessions/:sessionId" element={<RunSession />} />
              <Route path="loc/:locId/fire-drills" element={<FireDrillLogPage />} />
              <Route path="loc/:locId/fire-drills/:id" element={<FireDrillDetailPage />} />
              <Route path="loc/:locId/fire-drills/:id/live" element={<LiveDrillScreen />} />
              <Route path="loc/:locId/fire-drills/:id/wrap-up" element={<WrapUpScreen />} />
              <Route path="loc/:locId/incidents" element={<IncidentRegister />} />
              <Route path="loc/:locId/incidents/:incidentId" element={<IncidentDetail />} />
              <Route path="loc/:locId/medications" element={<LocationMedicationRegister />} />
              <Route path="loc/:locId/policies" element={<PolicyVault />} />
              <Route path="loc/:locId/policies/:policyId" element={<PolicyDetail />} />
              <Route path="org/:orgId/loc/:locId/policies" element={<PolicyVault />} />
              <Route path="org/:orgId/loc/:locId/policies/:policyId" element={<PolicyDetail />} />
              <Route
                path="loc/:locId/students/:studentId/medications/print"
                element={<StudentMedicationPrint />}
              />
              <Route path="loc/:locId/cleaning" element={<CleaningPage />} />
              <Route path="loc/:locId/checklists" element={<ChecklistsPage />} />
              <Route path="loc/:locId/checklists/sign-off" element={<ChecklistsPage />} />
              <Route path="loc/:locId/checklists/template" element={<ChecklistsPage />} />
              <Route path="org/:orgId/loc/:locId/checklists" element={<ChecklistsPage />} />
              <Route
                path="org/:orgId/loc/:locId/checklists/sign-off"
                element={<ChecklistsPage />}
              />
              <Route
                path="org/:orgId/loc/:locId/checklists/template"
                element={<ChecklistsPage />}
              />

              {/* Super admin scoped into an org: /org/:orgId/... */}
              <Route path="org/:orgId/dashboard" element={<Dashboard />} />
              <Route path="org/:orgId/locations" element={<Locations />} />
              <Route path="org/:orgId/forms" element={<Forms />} />
              <Route path="org/:orgId/forms/new" element={<FormBuilder />} />
              <Route path="org/:orgId/forms/:formId/edit" element={<FormBuilder />} />
              <Route path="org/:orgId/messages" element={<Messages />} />
              <Route path="org/:orgId/messages/:messageId" element={<MessageDetailPage />} />
              <Route
                path="org/:orgId/observations/:observationId"
                element={<ObservationDetailPage />}
              />
              <Route path="org/:orgId/team" element={<Team />} />

              {/* Super admin scoped into an org + location: /org/:orgId/loc/:locId/... */}
              <Route path="org/:orgId/loc/:locId/dashboard" element={<Dashboard />} />
              <Route path="org/:orgId/loc/:locId/students" element={<Students />} />
              <Route path="org/:orgId/loc/:locId/students/:id" element={<StudentDetail />} />
              <Route path="org/:orgId/loc/:locId/classes" element={<Classes />} />
              <Route path="org/:orgId/loc/:locId/calendar" element={<CalendarRoute />} />
              <Route path="org/:orgId/loc/:locId/forms" element={<Forms />} />
              <Route path="org/:orgId/loc/:locId/forms/new" element={<FormBuilder />} />
              <Route path="org/:orgId/loc/:locId/forms/:formId/edit" element={<FormBuilder />} />
              <Route path="org/:orgId/loc/:locId/forms/:formId/fill" element={<FormFill />} />
              <Route
                path="org/:orgId/loc/:locId/forms/:formId/thanks"
                element={<FormFillThankYou />}
              />
              <Route path="org/:orgId/loc/:locId/messages" element={<Messages />} />
              <Route
                path="org/:orgId/loc/:locId/messages/:messageId"
                element={<MessageDetailPage />}
              />
              <Route
                path="org/:orgId/loc/:locId/observations/:observationId"
                element={<ObservationDetailPage />}
              />
              <Route path="org/:orgId/loc/:locId/team" element={<Team />} />
              <Route path="org/:orgId/loc/:locId/sessions/:sessionId" element={<RunSession />} />
              <Route path="org/:orgId/loc/:locId/fire-drills" element={<FireDrillLogPage />} />
              <Route
                path="org/:orgId/loc/:locId/fire-drills/:id"
                element={<FireDrillDetailPage />}
              />
              <Route
                path="org/:orgId/loc/:locId/fire-drills/:id/live"
                element={<LiveDrillScreen />}
              />
              <Route
                path="org/:orgId/loc/:locId/fire-drills/:id/wrap-up"
                element={<WrapUpScreen />}
              />
              <Route path="org/:orgId/loc/:locId/incidents" element={<IncidentRegister />} />
              <Route
                path="org/:orgId/loc/:locId/incidents/:incidentId"
                element={<IncidentDetail />}
              />
              <Route
                path="org/:orgId/loc/:locId/medications"
                element={<LocationMedicationRegister />}
              />
              <Route
                path="org/:orgId/loc/:locId/students/:studentId/medications/print"
                element={<StudentMedicationPrint />}
              />
              <Route path="org/:orgId/loc/:locId/cleaning" element={<CleaningPage />} />

              {/* Parent (kid-as-frame) routes. The kid is the navigational
                axis; URL is the truth for active student via :studentId.
                ParentRoute guards the subtree (non-parent → /dashboard,
                unknown studentId → parentHomePath, 0 kids → onboarding). */}
              <Route path="parent" element={<ParentRoute />}>
                <Route index element={<ParentHomeRedirect />} />
                <Route path="onboarding" element={<ParentOnboarding />} />
                <Route path="choose" element={<ChildChooser />} />
                {/* Family pages: across every child, never change the active child. */}
                <Route path="family/inbox" element={<FamilyInbox />} />
                <Route path="family/policies" element={<MyPolicies />} />
                <Route path="family/threads/:threadId" element={<FamilyThreadPage />} />
                {/* Old All-children frame. Kept as redirects only. */}
                <Route path="all" element={<LegacyAllRedirect />} />
                <Route path="all/*" element={<LegacyAllRedirect />} />
                <Route
                  path=":studentId/observations/:observationId"
                  element={<ObservationDetailPage />}
                />
                <Route path=":studentId" element={<ParentChildHome />} />
                <Route path=":studentId/forms" element={<ParentChildForms />} />
                <Route path=":studentId/updates" element={<ParentChildUpdates />} />
                <Route path=":studentId/details" element={<ParentChildDetails />} />
                <Route
                  path=":studentId/forms/:invitationId/fill"
                  element={<ParentFormFillRedirect />}
                />
                <Route path=":studentId/absence/new" element={<AbsenceToCalendarRedirect />} />
                <Route path=":studentId/calendar" element={<ParentCalendar />} />
                <Route
                  path=":studentId/profile/pickup-contacts"
                  element={<ParentChildPickupContacts />}
                />
                <Route path="account/pickup-contacts" element={<ParentAccountPickupContacts />} />
                {/* Moments album, and the moment-detail deep-link target
                    (:momentId highlights that moment within the album). */}
                <Route path=":studentId/moments" element={<ParentMomentsPage />} />
                <Route path=":studentId/moments/:momentId" element={<ParentMomentsPage />} />
                <Route path=":studentId/incidents" element={<ParentChildIncidents />} />
                <Route path=":studentId/incidents/:incidentId" element={<ParentChildIncidents />} />
                <Route path=":studentId/medications" element={<ParentChildMedications />} />
                {/* Two-way threads with the team. The list route doubles as
                    the composer entry; :threadId opens the shared ThreadView. */}
                <Route path=":studentId/threads" element={<ParentChildThreads />} />
                <Route path=":studentId/threads/:threadId" element={<ParentChildThreads />} />
              </Route>

              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Route>

          {/* Fallback — unauthenticated users hitting unknown routes */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    </ToastProvider>
  );
}

export default App;
